import * as React from "react";
import { cn } from "../utils/cn";
import { textV4Variants } from "@src/lib/ui/Text";

const HtmlTable = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative overflow-auto">
    <table
      ref={ref}
      className={cn("caption-bottom text-s-v4", className)}
      {...props}
    />
  </div>
));
HtmlTable.displayName = "HtmlTable";

const HtmlTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
HtmlTableHeader.displayName = "HtmlTableHeader";

const HtmlTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
HtmlTableBody.displayName = "HtmlTableBody";

const HtmlTableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t border-border-primary bg-bg-secondary font-medium-v4 [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
));
HtmlTableFooter.displayName = "HtmlTableFooter";

const HtmlTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-border-primary transition-colors hover:bg-state-hover data-[state=selected]:bg-state-selected",
      className,
    )}
    {...props}
  />
));
HtmlTableRow.displayName = "HtmlTableRow";

const HtmlTableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      textV4Variants({ variant: "fine-print-title" }),
      "h-10 px-2 text-left align-middle text-text-tertiary [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
));
HtmlTableHead.displayName = "HtmlTableHead";

const HtmlTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "p-2 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
      className,
    )}
    {...props}
  />
));
HtmlTableCell.displayName = "HtmlTableCell";

const HtmlTableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-s-v4 text-text-tertiary", className)}
    {...props}
  />
));
HtmlTableCaption.displayName = "HtmlTableCaption";

export {
  HtmlTable,
  HtmlTableHeader,
  HtmlTableBody,
  HtmlTableFooter,
  HtmlTableHead,
  HtmlTableRow,
  HtmlTableCell,
  HtmlTableCaption,
};
